const MAX_REVIEW_PHOTO_SIZE = 5 * 1024 * 1024;
const ALLOWED_REVIEW_PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

class ProductReviewForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.form = this.querySelector('[data-review-form]');
    if (!this.form) return;

    this.submitButton = this.form.querySelector('[data-review-submit]');
    this.status = this.form.querySelector('[data-review-status]');
    this.ratingFieldset = this.form.querySelector('[data-rating-fieldset]');
    this.photoInput = this.form.querySelector('[data-review-photo]');
    this.fileStatus = this.form.querySelector('[data-file-status]');
    this.fileName = this.form.querySelector('[data-file-name]');
    this.removeFileButton = this.form.querySelector('[data-remove-file]');
    this.defaultSubmitText = this.submitButton?.textContent.trim() || 'ENVIAR RESEÑA';
    this.submitting = false;

    this.form.addEventListener('submit', this.handleSubmit);
    this.form.addEventListener('change', this.handleChange);
    this.removeFileButton?.addEventListener('click', this.clearPhoto);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.form?.removeEventListener('change', this.handleChange);
    this.removeFileButton?.removeEventListener('click', this.clearPhoto);
    this.initialized = false;
  }

  handleChange = (event) => {
    if (event.target.matches('input[name="calificacion"]')) {
      this.ratingFieldset.dataset.rating = event.target.value;
      this.setStatus('');
    }

    if (event.target === this.photoInput) {
      this.updatePhotoStatus();
    }
  };

  clearPhoto = () => {
    if (this.photoInput) this.photoInput.value = '';
    this.updatePhotoStatus();
    this.setStatus('');
  };

  updatePhotoStatus() {
    const file = this.photoInput?.files?.[0];
    if (!file) {
      if (this.fileName) this.fileName.textContent = '';
      if (this.fileStatus) this.fileStatus.hidden = true;
      return;
    }

    if (this.fileName) this.fileName.textContent = file.name;
    if (this.fileStatus) this.fileStatus.hidden = false;

    const photoError = this.getPhotoError(file);
    this.setStatus(photoError || '');
  }

  getPhotoError(file) {
    if (!file) return '';
    if (!ALLOWED_REVIEW_PHOTO_TYPES.has(file.type)) {
      return 'La foto debe ser JPG, PNG o WebP.';
    }
    if (file.size > MAX_REVIEW_PHOTO_SIZE) {
      return 'La foto no puede superar 5 MB.';
    }
    return '';
  }

  handleSubmit = async (event) => {
    event.preventDefault();
    if (this.submitting) return;

    this.setStatus('');

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    const photoError = this.getPhotoError(this.photoInput?.files?.[0]);
    if (photoError) {
      this.setStatus(photoError, 'error');
      this.photoInput?.focus();
      return;
    }

    this.setSubmitting(true);

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
      });

      if (!response.ok) {
        throw new Error(`Review request failed: ${response.status}`);
      }

      // The webhook may return an empty or non-JSON 2xx response. At this point
      // Shopify already received the review, so response parsing is best-effort.
      await response.json().catch(() => null);

      this.form.reset();
      delete this.ratingFieldset.dataset.rating;
      this.updatePhotoStatus();
      this.setStatus(
        '¡Gracias por tu opinión! Tu reseña fue recibida y será publicada después de revisarla.',
        'success'
      );
    } catch (error) {
      console.error('No se pudo enviar la reseña.', error);
      this.setStatus('No pudimos enviar tu reseña. Inténtalo nuevamente.', 'error');
    } finally {
      this.setSubmitting(false);
    }
  };

  setSubmitting(isSubmitting) {
    this.submitting = isSubmitting;
    if (!this.submitButton) return;

    this.submitButton.disabled = isSubmitting;
    this.submitButton.setAttribute('aria-busy', String(isSubmitting));
    this.submitButton.textContent = isSubmitting ? 'Enviando...' : this.defaultSubmitText;
  }

  setStatus(message, state = message ? 'error' : '') {
    if (!this.status) return;
    this.status.textContent = message;
    if (state) {
      this.status.dataset.state = state;
    } else {
      delete this.status.dataset.state;
    }
  }
}

if (!customElements.get('product-review-form')) {
  customElements.define('product-review-form', ProductReviewForm);
}
